import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bot, MessageSquare, Hash, ExternalLink } from 'lucide-react'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'
import Modal from './Modal'

interface BrandMention {
  brandId: string
  brandName: string
  isUserBrand: boolean
  position: number | null
}

interface QueryDetail {
  id: string
  queryText: string
  createdAt: string
  brandsMentioned: BrandMention[]
}

interface AIMentionData {
  score: number
  totalQueries: number
  queriesMentioned: number
  competitorQueriesMentioned: number
  mentionRate: number
  competitorMentionRate: number
  avgPosition: number | null
  competitorAvgPosition: number | null
  queries?: QueryDetail[]
}

interface AIMentionsCardProps {
  mentions: AIMentionData | null
}

export default function AIMentionsCard({ mentions }: AIMentionsCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [showTrendModal, setShowTrendModal] = useState(false)
  const navigate = useNavigate()

  // Demo trend data (6 months of mention rate history)
  const mentionRatePercent = mentions?.mentionRate || 0
  const trendData = [
    { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: mentionRatePercent * 0.7 },
    { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: mentionRatePercent * 0.78 },
    { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: mentionRatePercent * 0.85 },
    { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: mentionRatePercent * 0.91 },
    { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: mentionRatePercent * 0.96 },
    { date: new Date(Date.now()).toISOString(), value: mentionRatePercent }
  ]

  // Calculate month-over-month change
  const lastMonthValue = trendData[trendData.length - 2]?.value || mentionRatePercent
  const monthOverMonthChange = mentionRatePercent - lastMonthValue
  // Treat very small changes (< 0.1%) as "stable" to avoid showing "- 0.0%" or "+ 0.0%"
  const trend = Math.abs(monthOverMonthChange) < 0.1 ? 'stable' : monthOverMonthChange > 0 ? 'up' : 'down'

  if (!mentions) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 to-white p-4 rounded-lg ring-1 ring-indigo-200 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-gray-900">AI Mentions</h3>
          </div>
          <div className="text-2xl font-bold text-gray-400">—</div>
        </div>

        <p className="text-xs text-gray-600 mb-2">
          No AI mention data yet.
        </p>

        <p className="text-xs text-gray-500">
          0 mentions across 0 AI queries
        </p>
      </div>
    )
  }

  return (
    <>
      {/* Card */}
      <div
        className="bg-gradient-to-br from-indigo-50 to-white p-4 rounded-lg ring-1 ring-indigo-200 space-y-2 cursor-pointer hover:ring-2 hover:ring-indigo-300 transition-all"
        onClick={() => setModalOpen(true)}
      >
        {/* Row 1: Title + Key Metric */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-semibold text-gray-900">AI Rankings</h3>
            <TrendBadge
              hasHistoricalData={true}
              trend={trend}
              value={monthOverMonthChange}
              unit="%"
              decimals={1}
              showPlusSign={true}
              onClick={() => setShowTrendModal(true)}
            />
          </div>
          <div className="text-2xl font-bold text-indigo-600">
            {mentionRatePercent.toFixed(1)}%
          </div>
        </div>

        {/* Row 2: Takeaway/Analysis */}
        <p className="text-xs text-gray-600 mb-2">
          {(() => {
            const rate = mentionRatePercent
            if (rate === 0) return "Not appearing in AI responses yet."
            if (rate < 25) return "Low AI visibility - significant improvement needed."
            if (rate < 50) return "Moderate AI visibility - room for improvement."
            if (rate < 75) return "Strong AI presence across most queries."
            return "Excellent AI visibility - consistently mentioned."
          })()}
        </p>

        {/* Row 3: What was analyzed and result */}
        <p className="text-xs text-gray-500 mb-2">
          {mentions.queriesMentioned} mentions across {mentions.totalQueries} AI queries
        </p>

        {/* Row 4: Average position if available */}
        {mentions.avgPosition !== null && mentions.avgPosition > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs text-gray-500">Avg position:</span>
            <span className="text-xs font-medium text-indigo-700">
              #{mentions.avgPosition.toFixed(1)}
            </span>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="4xl">
        <Modal.Header onClose={() => setModalOpen(false)}>
          <Bot className="w-6 h-6 text-indigo-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI Ranking</h2>
            <p className="text-sm text-gray-600 mt-1">
              Your brand's visibility across AI assistant responses
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="space-y-6">

              {/* Visibility Breakdown - Head-to-Head */}
              <div>
                <div className="bg-gray-50 rounded-lg border border-gray-200 shadow p-4 md:p-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                    {/* Column 1: Total Queries */}
                    <div className="text-center p-3 md:p-0 bg-white md:bg-transparent rounded-lg md:rounded-none">
                      <div className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">
                        Total Queries
                      </div>
                      <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">
                        {mentions.totalQueries}
                      </div>
                      <div className="text-xs text-gray-500">
                        Analyzed
                      </div>
                    </div>

                    {/* Column 2: Brand Mention Rate */}
                    <div className="text-center border-t md:border-t-0 md:border-l md:border-r border-gray-300 pt-4 md:pt-0 md:px-4 space-y-3">
                      <div className="text-xs text-indigo-600 font-medium uppercase tracking-wide mb-2">
                        Your Mention Rate
                      </div>
                      <div className="flex items-center justify-center gap-2 md:gap-3">
                        <div className="text-3xl md:text-4xl font-bold text-indigo-600 mb-1">
                          {mentionRatePercent.toFixed(1)}%
                        </div>
                        <div className="bg-gray-50 text-center text-gray-400 text-sm md:text-md rounded-lg border border-gray-200 px-2 py-1">
                          {mentions.avgPosition !== null && mentions.avgPosition > 0
                              ? `#${mentions.avgPosition.toFixed(1)}`
                              : '-'}
                          <p className="text-[10px]">Avg Position</p>
                        </div>

                      </div>
                      <div className="text-xs text-gray-500">
                        {mentions.queriesMentioned} of {mentions.totalQueries} queries
                      </div>
                    </div>

                    {/* Column 3: Competitor Mention Rate */}
                    <div className="text-center border-t md:border-t-0 border-gray-300 pt-4 md:pt-0 space-y-3">
                      <div className="text-xs text-red-600 font-medium uppercase tracking-wide mb-2">
                        Competitor Mention Rate
                      </div>
                      <div className="flex items-center justify-center gap-2 md:gap-3">
                        <div className="text-3xl md:text-4xl font-bold text-red-600 mb-1">
                          {(mentions.competitorMentionRate || 0).toFixed(1)}%
                        </div>
                        <div className="bg-gray-50 text-center text-gray-400 text-sm md:text-md rounded-lg border border-gray-200 px-2 py-1">
                        {mentions.competitorAvgPosition !== null && mentions.competitorAvgPosition > 0
                            ? `#${mentions.competitorAvgPosition.toFixed(1)}`
                            : '-'}
                          <p className="text-[10px]">Avg Position</p>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {mentions.competitorQueriesMentioned || 0} of {mentions.totalQueries} queries
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Position Analysis */}
              {mentions.avgPosition !== null && mentions.avgPosition > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <Hash className="w-4 h-4" />
                    <span>Position Analysis</span>
                  </h3>
                  <div className="bg-purple-50 rounded-lg border border-purple-200 p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <div className="text-sm text-purple-600 font-medium mb-1">
                          Average Mention Position
                        </div>
                        <div className="text-4xl font-bold text-purple-700">
                          #{mentions.avgPosition.toFixed(1)}
                        </div>
                      </div>
                      <MessageSquare className="w-12 h-12 text-purple-300" />
                    </div>
                    <p className="text-xs text-purple-700">
                      {mentions.avgPosition <= 3
                        ? "Excellent! Your brand appears in the first few sentences of AI responses."
                        : mentions.avgPosition <= 10
                        ? "Good positioning - your brand appears early in AI responses."
                        : "Your brand is mentioned, but appears later in responses. Consider improving content relevance."}
                    </p>
                  </div>
                </div>
              )}

              {/* Query Details */}
              {mentions.queries && mentions.queries.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>Query Details ({mentions.queries.length})</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <div className="max-h-96 overflow-y-auto space-y-3">
                      {mentions.queries.map((query) => (
                          <div
                            key={query.id}
                            onClick={() => navigate('/search-queries', { state: { openQueryId: query.id } })}
                            className="bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer group"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <p className="text-sm text-gray-900 font-medium flex-1">
                                "{query.queryText}"
                              </p>
                              <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-indigo-600 transition-colors ml-2 flex-shrink-0" />
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {query.brandsMentioned.length === 0 ? (
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">
                                  No brands mentioned
                                </span>
                              ) : (
                                <>
                                  {(() => {
                                    // De-duplicate brands by name, keeping best position
                                    const brandMap = new Map<string, BrandMention>();

                                    query.brandsMentioned.forEach(brand => {
                                      const existing = brandMap.get(brand.brandName);

                                      if (!existing) {
                                        brandMap.set(brand.brandName, brand);
                                      } else {
                                        // Keep the one with better position
                                        if (brand.position !== null && (existing.position === null || brand.position < existing.position)) {
                                          brandMap.set(brand.brandName, brand);
                                        }
                                      }
                                    });

                                    // Convert to array and sort by position (lowest/best first)
                                    return Array.from(brandMap.values()).sort((a, b) => {
                                      // Handle null positions (put them at the end)
                                      if (a.position === null && b.position === null) return 0;
                                      if (a.position === null) return 1;
                                      if (b.position === null) return -1;

                                      // Sort by position (lowest first)
                                      return a.position - b.position;
                                    });
                                  })().map((brand, idx) => (
                                    <span
                                      key={idx}
                                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        brand.isUserBrand
                                          ? 'bg-indigo-100 text-indigo-700 border border-indigo-300'
                                          : 'bg-orange-100 text-orange-700 border border-orange-200'
                                      }`}
                                    >
                                      {brand.brandName}
                                      {brand.position !== null && ` (#${idx+1})`}
                                    </span>
                                  ))}
                                </>
                              )}
                            </div>
                          </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
        </Modal.Body>
      </Modal>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="AI Rankings Trend"
        metricName="Mention Rate"
        data={trendData}
        color="#6366f1"
        unit="%"
      />
    </>
  )
}
